// Les réglages du bénévolat (D-127, Club › Bénévolat) : ce que les écrans demandent à la base. Le bureau seul règle (les
// fonctions et la règle clubs_update le vérifient) ; les parcours du club se lisent par tous ses comptes (Mes badges).
// Migrations 20261103100000_parcours_du_club.sql et 20261103110000_reglages_benevolat.sql.
import { supabase } from './supabase';
import { setClubParcours, type ClubParcoursRow } from '../domain/badges';

const db = () => {
  if (!supabase) throw new Error('Base non configurée');
  return supabase;
};
async function must<T = unknown>(q: PromiseLike<{ data: unknown; error: { message: string; code?: string } | null }>): Promise<T> {
  const { data, error } = await q;
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
  return data as T;
}
const rpc = <T>(fn: string, args: Record<string, unknown> = {}) => must<T>(db().rpc(fn, args));

// --- Les parcours du club ------------------------------------------------------------------------
let loaded: { club: string; at: number; rows: ClubParcoursRow[] } | null = null;
/** Les parcours réglés par le club (renommés, masqués, créés), posés dans le registre de src/domain/badges.ts. Relus au
 *  plus toutes les 5 minutes, ou après un réglage (`force`). Sans réponse : les parcours de base, tels quels. */
export async function ensureParcours(clubId: string, force = false): Promise<ClubParcoursRow[]> {
  if (!force && loaded?.club === clubId && Date.now() - loaded.at < 5 * 60_000) return loaded.rows;
  const rows = await rpc<ClubParcoursRow[]>('badge_parcours', { p_club: clubId }).catch(() => null);
  if (!rows) { if (loaded?.club !== clubId) setClubParcours([]); return loaded?.club === clubId ? loaded.rows : []; }
  loaded = { club: clubId, at: Date.now(), rows };
  setClubParcours(rows);
  return rows;
}
/** Créer (`key` vide) ou régler un parcours ; rend sa clé. Un parcours de base : seul le nom compte. */
export async function saveParcours(clubId: string, p: { key: string | null; name: string; picto: string | null; blurb: string | null; labels: string[] | null }): Promise<string> {
  const k = await rpc<string>('parcours_save', { p_club: clubId, p_key: p.key, p_name: p.name, p_picto: p.picto, p_blurb: p.blurb, p_labels: p.labels });
  await ensureParcours(clubId, true);
  return k;
}
export async function hideParcours(clubId: string, key: string, hidden: boolean): Promise<void> {
  await rpc('parcours_hide', { p_club: clubId, p_key: key, p_hidden: hidden });
  await ensureParcours(clubId, true);
}
export async function deleteParcours(clubId: string, key: string): Promise<void> {
  await rpc('parcours_delete', { p_club: clubId, p_key: key });
  await ensureParcours(clubId, true);
}
/** Ranger un nom de créneau dans un parcours ; null : son rangement tout seul. Les étoiles suivent. */
export const rangeLabel = (clubId: string, label: string, parcours: string | null) =>
  rpc('parcours_range', { p_club: clubId, p_label: label, p_parcours: parcours });

// --- Jeunes ------------------------------------------------------------------------------------------
/** Un nom de créneau et ce que prend un nouveau créneau de ce nom (`club` : réglé par le club ; `auto` : la règle de
 *  D-103 l'ouvre aux matchs des équipes de jeunes). */
export interface YouthLabel { label: string; label_key: string; open: boolean; club: boolean; auto: boolean; slots: number }
export const youthLabels = (clubId: string) => rpc<YouthLabel[]>('youth_labels', { p_club: clubId });
/** Ouvert ou non aux jeunes par défaut ; null : la règle de D-103 revient. */
export const setYouthLabel = (clubId: string, label: string, open: boolean | null) =>
  rpc('youth_label_set', { p_club: clubId, p_label: label, p_open: open });

// --- Groupes, annonces et relances : les réglages du club ----------------------------------------------
export interface VolunteerSettings {
  badges_bravo: boolean;
  badges_assiduite: boolean;
  volunteer_reminder_hour: number | null;
  group_create_open: boolean;
  group_trial_days: number;
  group_every_days: number;
}
export const DEFAULT_VOLUNTEER_SETTINGS: VolunteerSettings = {
  badges_bravo: true, badges_assiduite: true, volunteer_reminder_hour: 18, group_create_open: true, group_trial_days: 30, group_every_days: 60,
};
const COLS = 'badges_bravo, badges_assiduite, volunteer_reminder_hour, group_create_open, group_trial_days, group_every_days';
export async function volunteerSettings(clubId: string): Promise<VolunteerSettings> {
  const rows = await must<VolunteerSettings[]>(db().from('clubs').select(COLS).eq('id', clubId)).catch(() => []);
  return { ...DEFAULT_VOLUNTEER_SETTINGS, ...(rows[0] ?? {}) };
}
export const saveVolunteerSettings = (clubId: string, s: Partial<VolunteerSettings>) => must(db().from('clubs').update(s).eq('id', clubId));

// --- Les récompenses des grades (lecture) ------------------------------------------------------------------
export interface RewardRow { grade: string; picto: string; title: string; perk: boolean; parcours: string[] | null }
export const gradeRewards = (clubId: string) =>
  must<RewardRow[]>(db().from('grade_rewards').select('grade, picto, title, perk, parcours').eq('club_id', clubId));
